/**
 * MangaDashboard.tsx
 * Dashboard(6).tsx aligned version / Layout-fixed version
 * - api.dashboard.get() の DashData 構造に合わせた Manga Mode 用ダッシュボード
 * - KPI / 締切 / 未払い支出 / 最近の日報 / 人件費推移を実データ表示
 */
import { useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  BookOpen,
  Users,
  CreditCard,
  PiggyBank,
  FileText,
  AlertCircle,
  LogOut,
  ChevronRight,
} from 'lucide-react'
import { api } from '../api/client'
import { useAuth } from '../components/AuthGuard'
import './MangaDashboard.css'

// 下部の走るキャラクターは App.tsx の <RunningCharacters /> で共通表示します。
// MangaDashboard 内では描画しないことで、Admin MangaMode での重複と古い表示を防ぎます.

type DashData = {
  today: string
  upcoming_deadlines: any[]
  unpaid_expenses: any[]
  today_attendance: any[]
  pending_revenues: any[]
  recent_reports: any[]
  monthly_labor: {
    total: number; working_days: number; man_days: number; meal_total: number
    help_total?: number; combined_total?: number
  } | null
  monthly_revenue: { received: number; tax_reserved: number; pending_income: number } | null
  spendable: number
  labor_chart: { month: string; total: number; man_days: number }[]
}

const LABOR_TARGET = 450000
const DONE_STATUSES = new Set(['completed', 'submitted', 'hiatus'])

function isDoneEpisode(ep: any): boolean {
  const status = String(ep?.status ?? ep?.episode_status ?? '').toLowerCase()
  if (DONE_STATUSES.has(status)) return true

  const stagesDone = Number(ep?.stages_done ?? ep?.done_stages ?? NaN)
  const stagesTotal = Number(ep?.stages_total ?? ep?.total_stages ?? 7)
  if (Number.isFinite(stagesDone) && stagesDone >= stagesTotal && stagesTotal > 0) return true

  const summary = String(ep?.stage_summary ?? '')
  if (summary) {
    const pairs = summary.split(',').map((pair) => pair.split(':'))
    const known = pairs.filter(([stage, st]) => stage && st)
    if (known.length > 0 && known.every(([, st]) => st === 'done')) return true
  }

  return false
}

function daysText(days: number | null | undefined) {
  if (days == null) return '—'
  if (days < 0) return `${Math.abs(days)}日超過`
  if (days === 0) return '今日'
  return `残${days}日`
}

function formatYen(n: number | null | undefined) {
  return Number(n ?? 0).toLocaleString('ja-JP')
}

function compactYen(n: number) {
  if (Math.abs(n) >= 10000) return `${Math.round(n / 10000)}万`
  return String(n)
}

function formatMonthLabel(month: string) {
  if (!month) return ''
  const parts = month.split('-')
  if (parts.length >= 2) return `${Number(parts[1])}月`
  return month
}


export default function MangaDashboard() {
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const [isLoaded, setIsLoaded] = useState(false)
  const [data, setData] = useState<DashData | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    const timer = window.setTimeout(() => setIsLoaded(true), 100)
    return () => window.clearTimeout(timer)
  }, [])

  useEffect(() => {
    api.dashboard.get()
      .then((res: DashData) => {
        setData(res)
        setErr(null)
      })
      .catch((e: any) => setErr(e?.message ?? 'ダッシュボードの取得に失敗しました'))
  }, [])

  const computed = useMemo(() => {
    if (!data) return null

    const activeDeadlines = data.upcoming_deadlines.filter((e) => !isDoneEpisode(e))
    const criticalDeadlines = activeDeadlines.filter((e) => (e.days_until_submit ?? 99) <= 2)
    const overdueExpenses = data.unpaid_expenses.filter((e) => (e.days_until_due ?? 0) < 0)

    const mealTotal = data.monthly_labor?.meal_total ?? 0
    const normalLaborTotal = (data.monthly_labor?.total ?? 0) + mealTotal
    const helpLaborTotal = data.monthly_labor?.help_total ?? 0
    // 人件費（通常アシスタント + ヘルプスタッフの合計）。目標比較・グラフもこの合計値を使用
    const laborTotal = normalLaborTotal + helpLaborTotal
    const manDays = data.monthly_labor?.man_days ?? 0
    const laborPct = Math.round((laborTotal / LABOR_TARGET) * 100)

    const received = data.monthly_revenue?.received ?? 0
    const taxReserved = data.monthly_revenue?.tax_reserved ?? 0
    const pending = data.monthly_revenue?.pending_income ?? 0
    const spendable = data.spendable ?? 0

    const fixedExpenseTotal = data.unpaid_expenses
      .filter((e: any) => e.is_fixed)
      .reduce((s: number, e: any) => s + Number(e.amount ?? 0), 0)

    const variableExpenseTotal = data.unpaid_expenses
      .filter((e: any) => !e.is_fixed)
      .reduce((s: number, e: any) => s + Number(e.amount ?? 0), 0)

    const hasAlert = criticalDeadlines.length > 0 || overdueExpenses.length > 0

    const alertTitle = criticalDeadlines[0]
      ? `${criticalDeadlines[0].work_title} #${criticalDeadlines[0].episode_number}`
      : overdueExpenses[0]
        ? overdueExpenses[0].description
        : '確認事項なし'

    const alertKind = criticalDeadlines[0] ? '締切' : overdueExpenses[0] ? '支払超過' : '状態'
    const alertDays = criticalDeadlines[0]
      ? daysText(criticalDeadlines[0].days_until_submit)
      : overdueExpenses[0]
        ? daysText(overdueExpenses[0].days_until_due)
        : 'OK'

    const maxLabor = Math.max(
      LABOR_TARGET,
      laborTotal,
      ...data.labor_chart.map((d) => Number(d.total ?? 0)),
      1,
    )

    return {
      activeDeadlines,
      criticalDeadlines,
      overdueExpenses,
      laborTotal,
      manDays,
      laborPct,
      helpLaborTotal,
      received,
      taxReserved,
      pending,
      spendable,
      fixedExpenseTotal,
      variableExpenseTotal,
      hasAlert,
      alertTitle,
      alertKind,
      alertDays,
      maxLabor,
    }
  }, [data])

  if (err) {
    return (
      <div className="manga-dashboard manga-dashboard-state">
        <div className="manga-state-card">
          <h2>ERROR</h2>
          <p>{err}</p>
        </div>
      </div>
    )
  }

  if (!data || !computed) {
    return (
      <div className="manga-dashboard manga-dashboard-state">
        <div className="manga-state-card">
          <h2>LOADING</h2>
          <p>ダッシュボードを読み込んでいます...</p>
        </div>
      </div>
    )
  }

  const chartData = data.labor_chart.length > 0
    ? data.labor_chart.slice(-3)
    : [
      { month: '03', total: 0, man_days: 0 },
      { month: '04', total: 0, man_days: 0 },
      { month: '05', total: computed.laborTotal, man_days: computed.manDays },
    ]

  const topDeadline = computed.activeDeadlines[0]
  const unpaidTop = data.unpaid_expenses.slice(0, 5)

  return (
    <div className="manga-dashboard">
      <div className="manga-floating-circle manga-floating-circle-1" />
      <div className="manga-floating-circle manga-floating-circle-2" />
      <div className="manga-floating-circle manga-floating-circle-3" />

      <aside className="manga-sidebar">
        <div className="manga-sidebar-logo">
          <h1>Manga<span>OS</span></h1>
        </div>

        <nav className="manga-sidebar-nav">
          <div className="manga-nav-group">Main</div>
          <MangaNavItem icon={<LayoutDashboard size={18} />} label="ダッシュボード" active onClick={() => navigate('/')} />

          <div className="manga-nav-group manga-nav-group-spaced">Production</div>
          <MangaNavItem icon={<BookOpen size={18} />} label="作品・話数" onClick={() => navigate('/works')} />

          <div className="manga-nav-group manga-nav-group-spaced">Staff</div>
          <MangaNavItem icon={<Users size={18} />} label="アシスタント" onClick={() => navigate('/personnel')} />

          <div className="manga-nav-group manga-nav-group-spaced">Finance</div>
          <MangaNavItem icon={<CreditCard size={18} />} label="支払管理" onClick={() => navigate('/expenses')} />
          <MangaNavItem icon={<PiggyBank size={18} />} label="収入・税積立" onClick={() => navigate('/finance')} />
          <MangaNavItem icon={<FileText size={18} />} label="月次レポート" onClick={() => navigate('/monthly-report')} />
        </nav>
      </aside>

      <main className="manga-main">
        <header className="manga-header">
          <h2 className="manga-fade-up" style={{ animationDelay: '0.1s' }}>
            ダッシュボード
          </h2>

          <div className="manga-header-actions manga-fade-up" style={{ animationDelay: '0.2s' }}>
            <div className="manga-header-email">{user?.email ?? ''}</div>
            <button type="button" className="manga-logout-button manga-sweep" onClick={logout}>
              <span>ログアウト</span>
              <LogOut size={16} />
            </button>
          </div>
        </header>

        <div className="manga-content">
          <section
            className={`manga-alert manga-fade-up${computed.hasAlert ? '' : ' manga-alert-ok'}`}
            style={{ animationDelay: '0.3s' }}
            onClick={() => computed.criticalDeadlines.length > 0 ? navigate('/works') : navigate('/expenses')}
          >
            <div className="manga-alert-shadow" />
            <div className="manga-alert-body">
              <div className="manga-alert-left">
                <div className="manga-alert-icon">
                  <AlertCircle size={24} strokeWidth={3} />
                </div>
                <div>
                  <h3>{computed.hasAlert ? '要確認' : '確認事項なし'}</h3>
                  <p><span>{computed.alertKind}</span>{computed.alertTitle}</p>
                </div>
              </div>
              <div className="manga-alert-days">{computed.alertDays}</div>
            </div>
          </section>

          <section className="manga-kpi-grid">
            <KpiCard
              title="使えるお金"
              amount={formatYen(computed.spendable)}
              subtext="入金済 - 税積立 - 未払固定費"
              color={computed.spendable >= 0 ? '#59a674' : '#e3483b'}
              delay="0.4s"
              onClick={() => navigate('/finance')}
            />
            <KpiCard
              title="今月入金済"
              amount={formatYen(computed.received)}
              subtext={`入金待ち ¥${formatYen(computed.pending)}`}
              color="#1a1a1a"
              delay="0.5s"
              onClick={() => navigate('/finance')}
            />
            <KpiCard
              title="税積立 (先取)"
              amount={formatYen(computed.taxReserved)}
              subtext={computed.received > 0 ? `${Math.round(computed.taxReserved / computed.received * 100)}%` : '—'}
              color="#59a674"
              delay="0.6s"
              onClick={() => navigate('/finance')}
            />
            <KpiCard
              title="今月人件費"
              amount={formatYen(computed.laborTotal)}
              subtext={
                `目標の${computed.laborPct}% / ${computed.manDays}人足`
                + (computed.helpLaborTotal > 0 ? `（うちヘルプ ¥${formatYen(computed.helpLaborTotal)}）` : '')
              }
              color="#26438f"
              bgColor="#e9cf3c"
              delay="0.7s"
              onClick={() => navigate('/personnel')}
            />
          </section>

          <section className="manga-main-grid">
            <div className="manga-left-column">
              <div className="manga-chart-block manga-fade-up" style={{ animationDelay: '0.8s' }}>
                <div className="manga-chart-header">
                  <h3>人件費推移</h3>
                  <div>
                    <div className="manga-chart-caption">
                      THIS MONTH ¥{formatYen(computed.laborTotal)} / GOAL ¥{formatYen(LABOR_TARGET)}
                    </div>
                    <div className="manga-chart-percent">{computed.laborPct}%</div>
                  </div>
                </div>

                <div className="manga-chart-area">
                  <div className="manga-goal-line" />
                  <span className="manga-goal-label">GOAL {formatYen(LABOR_TARGET)}</span>

                  {chartData.map((d, index) => {
                    const total = Number(d.total ?? 0)
                    const height = isLoaded ? Math.max(4, Math.round((total / computed.maxLabor) * 190)) : 0
                    const over = total > LABOR_TARGET
                    return (
                      <ChartBar
                        key={`${d.month}-${index}`}
                        label={formatMonthLabel(d.month)}
                        value={compactYen(total)}
                        barClass={over ? 'manga-bar-red' : 'manga-bar-blue'}
                        height={height}
                        large={over}
                        small={!over}
                      />
                    )
                  })}
                </div>
                <div className="manga-chart-bottom-line" />
              </div>

              <div className="manga-pie-grid manga-fade-up" style={{ animationDelay: '0.9s' }}>
                <div>
                  <h3 className="manga-small-heading">収支構造</h3>
                  <div className="manga-no-data manga-hover-shadow manga-sweep-card">
                    {computed.received <= 0 ? (
                      <span>NO DATA</span>
                    ) : (
                      <CashflowMini
                        received={computed.received}
                        taxReserved={computed.taxReserved}
                        labor={computed.laborTotal}
                        fixedExpense={computed.fixedExpenseTotal}
                        spendable={computed.spendable}
                      />
                    )}
                  </div>
                </div>
                <div>
                  <h3 className="manga-small-heading">支出内訳</h3>
                  <div className="manga-expense-breakdown manga-hover-shadow manga-sweep-card">
                    <ExpenseBreakdown
                      labor={computed.laborTotal}
                      fixed={computed.fixedExpenseTotal}
                      variable={computed.variableExpenseTotal}
                      tax={computed.taxReserved}
                    />
                  </div>
                </div>
              </div>

              <div className="manga-fade-up" style={{ animationDelay: '1.0s' }}>
                <h3 className="manga-section-heading">
                  <span>最近のアクティビティ</span>
                </h3>
                <div className="manga-activity-list manga-hover-shadow">
                  {data.recent_reports.length === 0 ? (
                    <div className="manga-empty-row">最近の日報はありません</div>
                  ) : (
                    data.recent_reports.slice(0, 3).map((r: any) => (
                      <ActivityRow
                        key={r.id}
                        time={String(r.report_date ?? '').slice(5).replace('-', '/')}
                        name={r.assistant_name ?? 'アシスタント'}
                        action={r.issues ? '確認事項があります' : '日報を提出しました'}
                        target={`${r.stage ?? '作業'} ${r.pages_done ? `${r.pages_done}P` : ''}`}
                        dotColor={r.issues ? '#e3483b' : '#59a674'}
                        onClick={() => navigate('/reports')}
                      />
                    ))
                  )}
                </div>
              </div>
            </div>

            <aside className="manga-right-column">
              <div className="manga-fade-up" style={{ animationDelay: '1.1s' }}>
                <h3 className="manga-section-heading">
                  <span>締切管理</span>
                  {computed.criticalDeadlines.length > 0 && <em>{computed.criticalDeadlines.length}件緊急</em>}
                </h3>

                {topDeadline ? (
                  <div className="manga-deadline-card manga-hover-shadow manga-sweep-card" onClick={() => navigate('/works')}>
                    <div className="manga-deadline-dot" />
                    <div className="manga-deadline-body">
                      <div className="manga-deadline-title-row">
                        <div className="manga-deadline-title">
                          {topDeadline.work_title} <span>#{topDeadline.episode_number}</span>
                        </div>
                        <strong>{daysText(topDeadline.days_until_submit)}</strong>
                      </div>
                      <StagePips summary={topDeadline.stage_summary} />
                    </div>
                  </div>
                ) : (
                  <div className="manga-empty-card manga-hover-shadow">21日以内の締切なし</div>
                )}
              </div>

              <div className="manga-fade-up" style={{ animationDelay: '1.2s' }}>
                <h3 className="manga-section-heading">
                  <span>未払い支出</span>
                </h3>
                <div className="manga-expense-list manga-hover-shadow">
                  {unpaidTop.length === 0 ? (
                    <div className="manga-empty-row">未払いなし</div>
                  ) : (
                    unpaidTop.map((e: any) => (
                      <ExpenseItem
                        key={e.id}
                        title={e.description}
                        date={e.due_date ?? e.month ?? ''}
                        amount={formatYen(e.amount)}
                        dotColor={(e.days_until_due ?? 99) < 0 ? '#e3483b' : (e.days_until_due ?? 99) <= 5 ? '#e9cf3c' : '#59a674'}
                        onClick={() => navigate('/expenses')}
                      />
                    ))
                  )}

                  <button type="button" className="manga-view-all-button manga-sweep" onClick={() => navigate('/expenses')}>
                    VIEW ALL <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            </aside>
          </section>
        </div>
      </main>
    </div>
  )
}

function MangaNavItem({
  icon,
  label,
  active,
  onClick,
}: {
  icon: ReactNode
  label: string
  active?: boolean
  onClick: () => void
}) {
  return (
    <button type="button" className={`manga-nav-item manga-sweep${active ? ' active' : ''}`} onClick={onClick}>
      <span>{icon}</span>
      <strong>{label}</strong>
      {active && <i />}
    </button>
  )
}

function KpiCard({
  title,
  amount,
  subtext,
  color,
  bgColor = 'white',
  delay,
  onClick,
}: {
  title: string
  amount: string
  subtext: string
  color: string
  bgColor?: string
  delay: string
  onClick?: () => void
}) {
  return (
    <div className="manga-kpi-wrap manga-fade-up" style={{ animationDelay: delay }}>
      <div className="manga-kpi-shadow" />
      <button type="button" className="manga-kpi-card manga-sweep-card" style={{ backgroundColor: bgColor }} onClick={onClick}>
        <h3>{title}</h3>
        <div className="manga-kpi-amount" style={{ color }}>
          <span>¥</span>{amount}
        </div>
        <p>{subtext}</p>
      </button>
    </div>
  )
}

function ChartBar({
  label,
  value,
  height,
  barClass,
  small,
  large,
}: {
  label: string
  value: string
  height: number
  barClass: string
  small?: boolean
  large?: boolean
}) {
  return (
    <div className="manga-chart-item">
      <div className={`manga-chart-value${large ? ' large' : ''}${small ? ' small' : ''}`}>{value}</div>
      <div
        className={`manga-chart-bar ${barClass}${large ? ' large' : ''}`}
        style={{ height }}
      />
      <div className={`manga-chart-label${large ? ' large' : ''}`}>{label}</div>
    </div>
  )
}

function StagePips({ summary }: { summary?: string }) {
  const stages = ['name', 'name_revision', 'sketch', 'inking', 'background', 'finishing', 'submitted']
  const map: Record<string, string> = {}

  for (const pair of (summary ?? '').split(',')) {
    const [s, st] = pair.split(':')
    if (s && st) map[s] = st
  }

  return (
    <div className="manga-progress-pips">
      {stages.slice(0, 4).map((stage) => (
        <div key={stage} className={map[stage] === 'done' ? 'done' : map[stage] === 'in_progress' ? 'progress' : ''} />
      ))}
    </div>
  )
}

function ExpenseItem({
  title,
  date,
  amount,
  dotColor,
  onClick,
}: {
  title: string
  date: string
  amount: string
  dotColor: string
  onClick?: () => void
}) {
  return (
    <button type="button" className="manga-expense-item manga-sweep-row" onClick={onClick}>
      <span className="manga-expense-dot" style={{ backgroundColor: dotColor }} />
      <div>
        <strong>{title}</strong>
        <small>{date}</small>
      </div>
      <b>¥{amount}</b>
    </button>
  )
}

function ActivityRow({
  time,
  name,
  action,
  target,
  dotColor,
  onClick,
}: {
  time: string
  name: string
  action: string
  target: string
  dotColor: string
  onClick?: () => void
}) {
  return (
    <button type="button" className="manga-activity-row manga-sweep-row" onClick={onClick}>
      <div className="manga-activity-time">{time}</div>
      <div className="manga-activity-main">
        <div className="manga-activity-line">
          <span style={{ backgroundColor: dotColor }} />
          <strong>{name}</strong>
          <em>{action}</em>
        </div>
        <div className="manga-activity-target">{target}</div>
      </div>
      <ChevronRight size={16} className="manga-activity-chevron" />
    </button>
  )
}

function CashflowMini({
  received,
  taxReserved,
  labor,
  fixedExpense,
  spendable,
}: {
  received: number
  taxReserved: number
  labor: number
  fixedExpense: number
  spendable: number
}) {
  const pct = (v: number) => Math.max(0, Math.min(100, Math.round(v / received * 100)))
  const items = [
    { label: '税積立', value: taxReserved, color: '#e3483b', p: pct(taxReserved) },
    { label: '人件費', value: labor, color: '#26438f', p: pct(labor) },
    { label: '固定費', value: fixedExpense, color: '#1a1a1a', p: pct(fixedExpense) },
    { label: '残', value: Math.max(0, spendable), color: '#59a674', p: pct(Math.max(0, spendable)) },
  ].filter((item) => item.p > 0)

  return (
    <div className="manga-cashflow-mini">
      <div className="manga-cashflow-bar">
        {items.map((item) => (
          <div key={item.label} style={{ width: `${item.p}%`, backgroundColor: item.color }} />
        ))}
      </div>
      <div className="manga-cashflow-legend">
        {items.map((item) => (
          <div key={item.label}>
            <span style={{ backgroundColor: item.color }} />
            {item.label} {item.p}%
          </div>
        ))}
      </div>
    </div>
  )
}

function ExpenseBreakdown({
  labor,
  fixed,
  variable,
  tax,
}: {
  labor: number
  fixed: number
  variable: number
  tax: number
}) {
  const total = labor + fixed + variable + tax

  if (total <= 0) {
    return (
      <>
        <div className="manga-pie"><div /></div>
        <div className="manga-legend">
          <div><span className="manga-dot-blue" />データなし</div>
        </div>
      </>
    )
  }

  const laborPct = Math.round(labor / total * 100)
  const fixedPct = Math.round(fixed / total * 100)

  return (
    <>
      <div className="manga-pie">
        <div style={{ transform: `rotate(${Math.max(20, laborPct * 3.6)}deg)` }} />
      </div>
      <div className="manga-legend">
        <div><span className="manga-dot-blue" />人件費 {laborPct}%</div>
        <div><span className="manga-dot-black" />固定費 {fixedPct}%</div>
      </div>
    </>
  )
}
